import React from 'react';
import { GenericFormPage } from '../../components/generic';
import { useTodo, Todo } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

const Form: React.FC = () => {
  type FormTodo = Omit<Todo, 'clientId' | 'todoTypeId' | 'todoRecurrenceIntervalId'> & {
    clientId?: string | number;
    todoTypeId?: string | number;
    todoRecurrenceIntervalId?: string | number;
  };

  const configWithOptions = (() => {
    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'clientId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Client',
          placeholder: 'Select a client (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/crm-clients',
          labelField: 'clientName',
          valueField: 'clientId' as const,
        };
      }
      if (field.name === 'todoTypeId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Type',
          placeholder: 'Select type (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/p-crm-todo-type',
          labelField: 'tt_descr',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'todoRecurrenceIntervalId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Repeat every',
          placeholder: 'Select interval (required if recurring)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/p-crm-todo-recurrence-interval',
          labelField: 'tri_descr',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'todoDueDate') {
        return {
          ...field,
          type: 'datetime' as const,
          label: 'Due Date',
          placeholder: 'Select due date (required if not recurring)',
        };
      }
      return field;
    });

    const customMapItemToFormData = (item: Todo): Partial<FormTodo> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        clientId: baseData.clientId != null ? String(baseData.clientId) : '',
        todoTypeId: baseData.todoTypeId != null ? String(baseData.todoTypeId) : '',
        todoRecurrenceIntervalId: baseData.todoRecurrenceIntervalId != null ? String(baseData.todoRecurrenceIntervalId) : '',
      };
    };

    const customGetInitialFormData = (): Partial<FormTodo> => {
      return {
        ...getInitialFormData(),
        clientId: '',
        todoTypeId: '',
        todoRecurrenceIntervalId: '',
      };
    };

    const toNum = (v: any): number | undefined =>
      v !== undefined && v !== null && String(v).trim() !== '' ? Number(v) : undefined;

    const customCreate = async (data: Partial<FormTodo>, files?: File[], filesToDetach?: number[]) => {
      const recurring = data.todoRecurring === true;
      
      if (recurring && !data.todoRecurrenceIntervalId) {
        throw new Error('Recurrence interval is required when recurring is enabled');
      }
      
      const processedData: Partial<Todo> = {
        ...data,
        clientId: toNum(data.clientId),
        todoTypeId: toNum(data.todoTypeId),
        todoRecurring: recurring || false,
        todoRecurrenceIntervalId: recurring ? toNum(data.todoRecurrenceIntervalId) : undefined,
        todoRecurrenceDayOfMonth: recurring ? toNum(data.todoRecurrenceDayOfMonth) : undefined,
        todoDueDate: !recurring ? data.todoDueDate : undefined,
      };
      return await moduleConfig.form.create(processedData, files, filesToDetach);
    };

    const customUpdate = async (id: number, data: Partial<FormTodo>, files?: File[], filesToDetach?: number[]) => {
      const recurring = data.todoRecurring === true;
      
      if (recurring && !data.todoRecurrenceIntervalId) {
        throw new Error('Recurrence interval is required when recurring is enabled');
      }
      
      const processedData: Partial<Todo> = {
        ...data,
        clientId: toNum(data.clientId),
        todoTypeId: toNum(data.todoTypeId),
        todoRecurring: recurring || false,
        todoRecurrenceIntervalId: recurring ? toNum(data.todoRecurrenceIntervalId) : undefined,
        todoRecurrenceDayOfMonth: recurring ? toNum(data.todoRecurrenceDayOfMonth) : undefined,
        todoDueDate: !recurring ? data.todoDueDate : undefined,
      };
      return await moduleConfig.form.update(id, processedData, files, filesToDetach);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData: customGetInitialFormData as () => Partial<Todo>,
      mapItemToFormData: customMapItemToFormData as (item: Todo) => Partial<Todo>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<Todo>, files?: File[], filesToDetach?: number[]) => Promise<Todo>,
        update: customUpdate as (id: number, data: Partial<Todo>, files?: File[], filesToDetach?: number[]) => Promise<Todo>,
      },
    };
  })();

  return (
    <GenericFormPage<Todo>
      useData={useTodo}
      config={configWithOptions}
    />
  );
};

export default Form;
